import type { ObjectBaseReportRowType } from '@econtractor/types';
import type { TFunction } from 'i18next';
import type { ReportPeriodType } from '../../ReportDate/periodUtils';
import type { ReportVatFilter } from '../../types';
import {
    exportDate,
    exportReportWorkbook,
    formatExportPeriod,
    isExportLocalMode,
    moneyColumns,
    roundExportMoney,
    yesNoLabel,
    type ExportCell,
    type ExportColumn,
} from '../../helper/exportReportWorkbook';

export interface ExportObjectsReportParams {
    objects: ObjectBaseReportRowType[];
    dates: [number, number] | null;
    period: ReportPeriodType;
    vat: ReportVatFilter;
    includeObjects: boolean;
    includeSales: boolean;
    includeServiceSales: boolean;
    showSales: boolean;
    showMaterials: boolean;
    showServices: boolean;
    showOther: boolean;
    rate: number;
    t: TFunction;
}

const vatFilterLabel = (vat: ReportVatFilter, t: TFunction): string => {
    if (vat === 1) return t('reportFilter.withVat');
    if (vat === 0) return t('reportFilter.noVat');
    return t('reportFilter.all');
};

export const exportObjectsReport = async ({
    objects,
    dates,
    period,
    vat,
    includeObjects,
    includeSales,
    includeServiceSales,
    showSales,
    showMaterials,
    showServices,
    showOther,
    rate,
    t,
}: ExportObjectsReportParams): Promise<boolean> => {
    if (!objects.length) return false;

    const localMode = isExportLocalMode(rate);
    const incomeLabel = t('Objects:fields.income');
    const materialsLabel = t('Objects:fields.materials');
    const servicesLabel = t('Objects:fields.services');
    const otherLabel = t('Objects:fields.other');

    const columns: ExportColumn[] = [
        { key: 'name', header: t('Objects:fields.name'), width: 36 },
        { key: 'client', header: t('Objects:fields.client'), width: 28 },
        { key: 'finished', header: t('export.finished'), width: 16, kind: 'date' },
        { key: 'created', header: t('Objects:fields.created'), width: 16, kind: 'date' },
    ];

    if (showMaterials) columns.push(...moneyColumns('materials', 'materialsLocal', materialsLabel, rate));
    if (showServices) columns.push(...moneyColumns('services', 'servicesLocal', servicesLabel, rate));
    if (showOther) columns.push(...moneyColumns('other', 'otherLocal', otherLabel, rate));
    columns.push(...moneyColumns('income', 'incomeLocal', incomeLabel, rate));
    columns.push({ key: 'vat', header: t('export.vat'), width: 14 });

    const typeParts = [
        `${t('reportFilter.objects')}: ${yesNoLabel(includeObjects, t)}`,
    ];
    if (showSales) {
        typeParts.push(`${t('reportFilter.sales')}: ${yesNoLabel(includeSales, t)}`);
        typeParts.push(`${t('reportFilter.serviceSales')}: ${yesNoLabel(includeServiceSales, t)}`);
    }

    const rows = objects.map((objectRow) => {
        const row: Record<string, ExportCell> = {
            name: objectRow.objectName || objectRow.clientName || '',
            client: objectRow.clientName || '',
            finished: exportDate(objectRow.objectFinished),
            created: exportDate(objectRow.objectCreated),
            income: roundExportMoney(objectRow.objectPriceSummary),
            vat: Number(objectRow.objectVat) ? t('export.withVat') : t('export.withoutVat'),
        };
        if (!localMode) row.incomeLocal = roundExportMoney(objectRow.objectPriceSummaryRate);
        if (showMaterials) {
            row.materials = roundExportMoney(objectRow.materialsMargin);
            if (!localMode) row.materialsLocal = roundExportMoney(objectRow.materialsMarginRate);
        }
        if (showServices) {
            row.services = roundExportMoney(objectRow.servicesMargin);
            if (!localMode) row.servicesLocal = roundExportMoney(objectRow.servicesMarginRate);
        }
        if (showOther) {
            row.other = roundExportMoney(objectRow.otherMargin);
            if (!localMode) row.otherLocal = roundExportMoney(objectRow.otherMarginRate);
        }
        return row;
    });

    const title = t('objectIncomeTitle');
    await exportReportWorkbook({
        filePrefix: 'report-objects',
        sheetName: title,
        title,
        headerLines: [
            { label: t('export.period'), value: formatExportPeriod(t(`dateFilter.${period}`), dates) },
            { label: t('export.types'), value: typeParts.join('; ') },
            { label: t('export.vat'), value: vatFilterLabel(vat, t) },
        ],
        columns,
        rows,
        totalLabel: t('export.total'),
    });

    return true;
};
